import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { DropDownList } from "@progress/kendo-react-dropdowns";

export interface InvoiceDialogProps {
  open: boolean;
  onClose: () => void;
  invoice: any;
  organizationName?: string;
  organizationEmail?: string;
  organizationPhone?: string;
  onStatusUpdate?: (entityId: string, recType: string, status: string) => Promise<void>;
  isAdmin?: boolean;
}

const formatDate = (dateValue?: any): string => {
  if (!dateValue) return "-";
  if (typeof dateValue === "string" && /[a-zA-Z]/.test(dateValue)) return dateValue;
  
  let timestamp = dateValue;
  if (typeof dateValue === "string") {
    const isoDate = new Date(dateValue);
    if (!isNaN(isoDate.getTime())) {
      const day = isoDate.getDate().toString().padStart(2, "0");
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return `${day} ${months[isoDate.getMonth()]} ${isoDate.getFullYear()}`;
    }
    timestamp = Number(dateValue);
  }
  if (typeof timestamp === "number" && !isNaN(timestamp)) {
    const ms = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
    const date = new Date(ms);
    if (!isNaN(date.getTime())) {
      const day = date.getDate().toString().padStart(2, "0");
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return `${day} ${months[date.getMonth()]} ${date.getFullYear()}`;
    }
  }
  return String(dateValue);
};

const formatCurrency = (value?: number): string => {
  if (value === undefined || value === null || isNaN(Number(value))) return "0.00";
  return Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export function InvoiceDialog({ open, onClose, invoice, organizationName, organizationEmail, organizationPhone, onStatusUpdate, isAdmin = false }: InvoiceDialogProps) {
  if (!open || !invoice) return null;

  const statusOptions = [
    { value: "PENDING", label: "Pending" },
    { value: "PAID", label: "Paid" },
    { value: "CANCELLED", label: "Cancelled" }
  ];

  const currentStatus = invoice?.status || "PENDING";
  const [selectedStatus, setSelectedStatus] = useState(statusOptions.find(s => s.value === currentStatus) || statusOptions[1]);
  const [isUpdating, setIsUpdating] = useState(false);
  
  const hasDiscount = invoice?.discountAmount > 0;

  const handleStatusUpdate = async () => {
    if (!onStatusUpdate || selectedStatus.value === currentStatus) return;
    
    setIsUpdating(true);
    try {
      await onStatusUpdate(invoice.entityId, invoice.recType, selectedStatus.value);
    } catch (error) {
      console.error("Failed to update status:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case "PAID": return { bg: "rgba(16, 185, 129, 0.2)", text: "#059669" };
      case "PENDING": return { bg: "rgba(251, 191, 36, 0.2)", text: "#d97706" };
      case "CANCELLED": return { bg: "rgba(239, 68, 68, 0.2)", text: "#dc2626" };
      default: return { bg: "rgba(251, 191, 36, 0.2)", text: "#d97706" };
    }
  };

  const statusColor = getStatusColor(currentStatus);

  const dialogContent = (
    <Dialog className="billingInvoiceDialog" width={800}>
      <div style={{ 
        background: '#fff', 
        borderRadius: '8px', 
        width: '100%',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        overflow: 'hidden',
        maxHeight: '90vh' // Ensure it fits vertically
      }}>
        
        {/* Header - Compacted Padding */}
        <div style={{ 
          background: '#4f46e5', 
          padding: '20px 32px', 
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700', letterSpacing: '-0.5px' }}>INVOICE</h2>
              <span style={{ 
                background: statusColor.bg, 
                color: statusColor.text,
                padding: '4px 10px', 
                borderRadius: '6px', 
                fontSize: '11px', 
                fontWeight: '600',
                textTransform: 'uppercase',
                backdropFilter: 'blur(4px)',
                letterSpacing: '0.05em',
                border: `1px solid ${statusColor.text}40`
              }}>
                {currentStatus}
              </span>
              {isAdmin && onStatusUpdate && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <DropDownList
                    data={statusOptions}
                    textField="label"
                    dataItemKey="value"
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.value)}
                    disabled={isUpdating}
                    style={{
                      width: '130px',
                      fontSize: '12px',
                      borderRadius: '6px',
                      background: 'white'
                    }}
                  />
                  <Button
                    onClick={handleStatusUpdate}
                    disabled={isUpdating || selectedStatus.value === currentStatus}
                    style={{
                      background: selectedStatus.value === currentStatus ? '#94a3b8' : '#10b981',
                      color: 'white',
                      border: 'none',
                      padding: '6px 14px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                      cursor: selectedStatus.value === currentStatus ? 'not-allowed' : 'pointer',
                      opacity: isUpdating ? 0.7 : 1
                    }}
                  >
                    {isUpdating ? 'Updating...' : 'Update'}
                  </Button>
                </div>
              )}
            </div>
            <p style={{ margin: '6px 0 0', opacity: 0.8, fontSize: '13px' }}>#{invoice?.invoiceNo || 'INV-0000'}</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ margin: 0, opacity: 0.8, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Issued Date</p>
            <p style={{ margin: '2px 0 0', fontSize: '15px', fontWeight: '600' }}>{formatDate(invoice?.invoiceDate)}</p>
          </div>
        </div>

        {/* Info Cards - Compacted Padding & Gap */}
        <div style={{ padding: '24px 32px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', background: '#f8fafc' }}>
          <div style={{ background: 'white', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
            <p style={{ margin: 0, fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.05em' }}>Billed To</p>
            <p style={{ margin: '8px 0 0', fontSize: '15px', fontWeight: '700', color: '#1e293b' }}>{organizationName || "Customer"}</p>
            {organizationEmail && <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748b' }}>{organizationEmail}</p>}
            {organizationPhone && <p style={{ margin: '1px 0 0', fontSize: '12px', color: '#64748b' }}>{organizationPhone}</p>}
          </div>
          <div style={{ background: 'white', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
            <p style={{ margin: 0, fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.05em' }}>Billing Period</p>
            <p style={{ margin: '8px 0 0', fontSize: '15px', fontWeight: '700', color: '#1e293b' }}>
              {formatDate(invoice?.fromDate)}
            </p>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748b' }}>
              to {formatDate(invoice?.toDate)}
            </p>
          </div>
        </div>

        {/* Table - Compacted */}
        <div style={{ padding: '0 32px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '16px' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '12px 16px', background: '#f1f5f9', borderRadius: '6px 0 0 6px', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Item Description</th>
                <th style={{ textAlign: 'center', padding: '12px 16px', background: '#f1f5f9', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Users</th>
                <th style={{ textAlign: 'right', padding: '12px 16px', background: '#f1f5f9', borderRadius: '0 6px 6px 0', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '16px 16px', borderBottom: '1px solid #f1f5f9' }}>
                  <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#334155' }}>Monthly Subscription</p>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#94a3b8' }}>
                    Standard Plan charges for the period
                  </p>
                </td>
                <td style={{ textAlign: 'center', padding: '16px 16px', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ background: '#e0e7ff', color: '#4338ca', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                    {invoice?.billedUsers ?? 0}
                  </span>
                </td>
                <td style={{ textAlign: 'right', padding: '16px 16px', borderBottom: '1px solid #f1f5f9', fontSize: '14px', fontWeight: '600', color: '#334155' }}>
                  ₹{formatCurrency(invoice?.billAmount)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Summary - Compacted */}
        <div style={{ padding: '16px 32px 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ width: '280px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>Subtotal</span>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>₹{formatCurrency(invoice?.billAmount)}</span>
              </div>
              {hasDiscount && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ fontSize: '13px', color: '#059669' }}>Discount</span>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#059669' }}>-₹{formatCurrency(invoice.discountAmount)}</span>
                </div>
              )}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                background: '#f8fafc',
                padding: '12px',
                borderRadius: '8px',
                marginTop: '12px',
                border: '1px solid #e2e8f0'
              }}>
                <span style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>Total Due</span>
                <span style={{ fontSize: '20px', fontWeight: '800', color: '#4f46e5' }}>
                  ₹{formatCurrency(invoice?.finalBillAmount ?? invoice?.billAmount)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Compacted */}
        <div style={{ 
          padding: '16px 32px', 
          background: '#f8fafc', 
          borderTop: '1px solid #e2e8f0',
          display: 'flex', 
          justifyContent: 'flex-end'
        }}>
          <Button
            style={{ 
              background: '#4f46e5', 
              color: 'white', 
              border: 'none', 
              padding: '10px 24px', 
              borderRadius: '6px', 
              fontSize: '13px', 
              fontWeight: '600', 
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(79, 70, 229, 0.1)'
            }}
            onClick={onClose}
          >
            Close Invoice
          </Button>
        </div>
      </div>
    </Dialog>
  );

  return createPortal(dialogContent, document.body);
}

export default InvoiceDialog;
